import React, { useEffect } from 'react'

import { useLazyQuery, useMutation } from '@apollo/react-hooks'
import { useRouter } from '../../../hooks'
import { Query, Mutation } from '../../../api'
import { toastr } from 'react-redux-toastr'
import * as yup from 'yup'
import { Formik, Form } from 'formik'
import { Button } from '@/components/ui/button'
import { Loader2, Save } from 'lucide-react'
import Input from '../../../components/form/FormikInput'
import AddressForm from '../../../components/form/Address'
import ProjectInfoSkeleton from './ProjectInfoSkeleton'
import { useTranslation } from 'react-i18next'
import { languages } from '../../../constants/language.ts'
import PlacePicker from '../../../components/PlacePicker'

const ProjectInfo = () => {
  const { t } = useTranslation()

  const schema = yup.object().shape({
    name: yup
      .string()
      .min(3, t('Project_info_create_project_name_min'))
      .required(t('Project_info_create_project_name_required')),
    code: yup
      .string()
      .transform((v) => (v === '' ? undefined : v))
      .matches(/^[A-Z0-9]{2,6}$/, t('Project_info_code_invalid'))
      .notRequired(),
    description: yup.string().required(t('Project_info_create_project_description_required')),
    address: AddressForm.validationSchemaOptional,
  })

  const {
    history,
    match,
    query: { projectId },
  } = useRouter()

  const [loadProject, projectData] = useLazyQuery(Query.PROJECT, {
    variables: { id: projectId },
  })
  const [createProject, createProjectData] = useMutation(Mutation.CREATE_PROJECT, {
    refetchQueries: [{ query: Query.PROJECTS }],
    onCompleted: ({ createProject }) => {
      history.replace(`${match.url.replace('new', createProject.id)}`)
      toastr.success(t('Project_info_create_project_project_created'))
    },
    onError: (e) => console.error(e.message)
  })
  const [updateProject, updateProjectData] = useMutation(Mutation.UPDATE_PROJECT, {
    refetchQueries: [{ query: Query.PROJECTS }],
    variables: { projectId },
    onCompleted: () => {
      toastr.success(t('Project_info_create_project_project_updated'))
    },
  })

  useEffect(() => {
    if (projectId && projectId !== 'new') loadProject()
  }, [projectId])

  const isNew = projectId === 'new'
  const isSaving = createProjectData.loading || updateProjectData.loading
  const project = projectData.data ? projectData.data.project : {}

  // An existing project renders only once its values are in — reinitializing a
  // filled-in form would otherwise flash empty fields on first paint.
  if (!isNew && !projectData.data && !projectData.error) return <ProjectInfoSkeleton />

  let initialValues = {
    name: '',
    code: project.code || '',
    description: '',
    address: { ...AddressForm.initialValues },
  }

  return (
    <Formik
      validationSchema={schema}
      initialValues={{
        ...initialValues,
        ...project,
        code: project.code || '',
        // A project with no address comes back as address: null, which fails the
        // object schema and silently blocks submit — keep the empty shape instead.
        address: { ...AddressForm.initialValues, ...(project.address || {}) },
      }}
      enableReinitialize
      initialTouched={false}
      onSubmit={(values) => {
        if (projectId === 'new') {
          const { code, ...createValues } = values
          createProject({
            variables: {
              ...createValues,
            },
          })
        } else {
          updateProject({
            variables: {
              id: projectId,
              ...values,
              code: values.code ? values.code.toUpperCase() : undefined,
            },
          })
        }
      }}
    >
      {({ setFieldValue }) => (
        <Form>
          <fieldset disabled={isSaving} className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <Input
                type="text"
                name="name"
                label={t('Project_info_name_label')}
                placeholder={t('Project_info_name_placeholder')}
              />
              {!isNew && (
                <div className="space-y-1.5">
                  <Input
                    type="text"
                    name="code"
                    label={t('Project_info_code_label')}
                    placeholder="SKY"
                  />
                  <p className="text-xs text-muted-foreground">{t('Project_info_code_hint')}</p>
                </div>
              )}
              <Input
                type="textarea"
                rows={5}
                name="description"
                label={t('Project_info_description_label')}
                placeholder={t('Project_info_description_placeholder')}
              />
              <Input
                label={t('Project_info_tender_language_label', 'Tender Language')}
                type="select"
                name="language"
                labelField="name"
                valueField="code"
                placeholder={t(
                  'Project_info_tender_language_placeholder',
                  'Select Tender Language',
                )}
                options={languages}
              />
            </div>
            <div className="space-y-4">
              <PlacePicker
                onChange={(address, e) => {
                  const {
                    city,
                    street_name,
                    street_number,
                    zip,
                    latLng: { lat, lng },
                    country,
                  } = address

                  setFieldValue('address', {
                    city,
                    street: street_name + ' ' + street_number,
                    zip,
                    lat,
                    lng,
                    country,
                  })
                }}
              />
              <AddressForm name="address" />
            </div>
          </fieldset>
          <div className="mt-6">
            <Button type="submit" disabled={isSaving}>
              {isSaving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {isNew ? t('Project_info_create_project_btn') : t('Project_info_update_project_btn')}
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  )
}

export default ProjectInfo
